export const REDE_TIPOS = [
  { id: 'instagram', label: 'Instagram', placeholder: '@conta ou link', icon: '📷' },
  { id: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...', icon: '👍' },
  { id: 'tiktok', label: 'TikTok', placeholder: '@conta ou link', icon: '🎵' },
  { id: 'linkedin', label: 'LinkedIn', placeholder: 'Link do perfil', icon: '💼' },
  { id: 'youtube', label: 'YouTube', placeholder: 'Link do canal', icon: '▶️' },
  { id: 'whatsapp', label: 'WhatsApp', placeholder: '+351 912 345 678', icon: '💬' },
  { id: 'x', label: 'X (Twitter)', placeholder: '@conta ou link', icon: '𝕏' },
  { id: 'outro', label: 'Outra', placeholder: 'https://...', icon: '🔗' },
] as const

export type RedeTipo = (typeof REDE_TIPOS)[number]['id']

export type RedeSocialItem = {
  rede: RedeTipo
  valor: string
  /** Nome visível quando rede === 'outro' */
  nome?: string
}

export type ContatosData = {
  telefone?: string
  email?: string
  morada?: string
  redes: RedeSocialItem[]
}

const REDE_IDS = new Set<string>(REDE_TIPOS.map((r) => r.id))

export function isRedeTipo(value: string): value is RedeTipo {
  return REDE_IDS.has(value)
}

export function getRedeMeta(rede: RedeTipo) {
  return REDE_TIPOS.find((r) => r.id === rede) ?? REDE_TIPOS[REDE_TIPOS.length - 1]
}

export function getRedePlaceholder(rede: RedeTipo): string {
  return getRedeMeta(rede).placeholder
}

export function getRedeLabel(rede: RedeTipo, nome?: string): string {
  if (rede === 'outro') return nome?.trim() || 'Rede social'
  return getRedeMeta(rede).label
}

export function parseContatos(raw: string | null): ContatosData {
  if (!raw?.trim()) return { redes: [] }
  try {
    return normalizeContatosData(JSON.parse(raw) as Record<string, unknown>)
  } catch {
    return { redes: [] }
  }
}

export function normalizeContatosData(parsed: Record<string, unknown>): ContatosData {
  const telefone = str(parsed.telefone)
  const email = str(parsed.email)
  const morada = str(parsed.morada)
  const redes: RedeSocialItem[] = []

  if (Array.isArray(parsed.redes)) {
    for (const item of parsed.redes) {
      if (!item || typeof item !== 'object') continue
      const o = item as { rede?: string; valor?: string; nome?: string }
      const rede = str(o.rede)
      const valor = str(o.valor)
      const nome = str(o.nome)
      if (!rede || !valor || !isRedeTipo(rede)) continue
      redes.push({
        rede,
        valor,
        ...(rede === 'outro' && nome ? { nome } : {}),
      })
    }
  }

  const instagram = str(parsed.instagram)
  if (instagram && !redes.some((r) => r.rede === 'instagram')) {
    redes.push({ rede: 'instagram', valor: instagram })
  }

  const facebook = str(parsed.facebook)
  if (facebook && !redes.some((r) => r.rede === 'facebook')) {
    redes.push({ rede: 'facebook', valor: facebook })
  }

  return {
    ...(telefone ? { telefone } : {}),
    ...(email ? { email } : {}),
    ...(morada ? { morada } : {}),
    redes,
  }
}

export function serializeContatos(data: ContatosData): string {
  const redes = data.redes
    .map((r) => ({
      rede: r.rede,
      valor: r.valor.trim(),
      ...(r.rede === 'outro' && r.nome?.trim() ? { nome: r.nome.trim() } : {}),
    }))
    .filter((r) => r.valor)

  return JSON.stringify({
    ...(data.telefone?.trim() ? { telefone: data.telefone.trim() } : {}),
    ...(data.email?.trim() ? { email: data.email.trim() } : {}),
    ...(data.morada?.trim() ? { morada: data.morada.trim() } : {}),
    ...(redes.length ? { redes } : {}),
  })
}

function str(v: unknown): string {
  return v != null ? String(v).trim() : ''
}

function ensureHttp(url: string): string {
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url.replace(/^\/+/, '')}`
}

function profileUrl(base: string, handle: string): string {
  const h = handle.replace(/^@/, '').replace(/^\/+/, '')
  return `${base.replace(/\/$/, '')}/${h}`
}

export function buildRedeHref(rede: RedeTipo, valor: string): string | undefined {
  const v = valor.trim()
  if (!v) return undefined

  if (/^https?:\/\//i.test(v)) return v

  switch (rede) {
    case 'instagram':
      return profileUrl('https://instagram.com', v)
    case 'facebook':
      return ensureHttp(v.includes('.') ? v : `facebook.com/${v.replace(/^@/, '')}`)
    case 'tiktok':
      return profileUrl('https://www.tiktok.com', v.startsWith('@') ? v : `@${v.replace(/^@/, '')}`)
    case 'linkedin':
      return ensureHttp(v.includes('.') ? v : `linkedin.com/in/${v.replace(/^@/, '')}`)
    case 'youtube':
      return ensureHttp(v.includes('.') ? v : `youtube.com/${v.replace(/^@/, '')}`)
    case 'whatsapp': {
      const digits = v.replace(/\D/g, '')
      return digits ? `https://wa.me/${digits}` : undefined
    }
    case 'x':
      return profileUrl('https://x.com', v)
    case 'outro':
      return v.includes('.') ? ensureHttp(v) : undefined
    default:
      return undefined
  }
}

export type ContactRow = {
  key: string
  icon: string
  label: string
  value: string
  href?: string
  hint?: string
}

export function buildContactRows(data: ContatosData): ContactRow[] {
  const rows: ContactRow[] = []

  if (data.telefone) {
    rows.push({
      key: 'tel',
      icon: '📞',
      label: 'Telefone',
      value: data.telefone,
      href: `tel:${data.telefone.replace(/\s/g, '')}`,
      hint: 'Toque para ligar',
    })
  }

  if (data.email) {
    rows.push({
      key: 'email',
      icon: '✉️',
      label: 'E-mail',
      value: data.email,
      href: `mailto:${data.email}`,
      hint: 'Enviar mensagem',
    })
  }

  data.redes.forEach((item, index) => {
    const meta = getRedeMeta(item.rede)
    const href = buildRedeHref(item.rede, item.valor)
    const label = getRedeLabel(item.rede, item.nome)
    const displayValue =
      item.rede === 'facebook' && item.valor.startsWith('http') ? 'Ver página' : item.valor

    rows.push({
      key: `rede-${item.rede}-${index}`,
      icon: meta.icon,
      label,
      value: displayValue,
      ...(href ? { href, hint: 'Abrir no navegador' } : {}),
    })
  })

  if (data.morada) {
    rows.push({
      key: 'morada',
      icon: '📍',
      label: 'Morada',
      value: data.morada,
    })
  }

  return rows
}
